import { z } from 'zod';
import { DetailedReportBaselineSchema } from './detailed-reporting.schema.js';
import {
    REPORT_CATALOG,
    reportDefinitionSupportsSurface,
} from './report-catalog.js';

const reportFilterScalar = z.union([
    z.string().max(500),
    z.number().finite(),
    z.boolean(),
]);

const boundedRecord = (keySchema, valueSchema, maxKeys, label) => z.record(keySchema, valueSchema)
    .superRefine((value, ctx) => {
        if (Object.keys(value).length > maxKeys) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `At most ${maxKeys} ${label} are allowed` });
        }
    });

export const ReportRunQuerySchema = z.object({
    search: z.string().max(200).optional(),
    filters: boundedRecord(
        z.string().max(150),
        z.array(reportFilterScalar).max(100),
        50,
        'filters',
    ).optional(),
    serverFilters: boundedRecord(
        z.string().max(160),
        z.union([reportFilterScalar, z.array(reportFilterScalar).max(100)]),
        50,
        'server filters',
    ).optional(),
    sortBy: z.string().trim().min(1).max(80).optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
    filterSummary: z.string().max(4000).optional(),
}).strict();

const filteredScopeSchema = z.object({
    mode: z.literal('filtered'),
    query: ReportRunQuerySchema,
}).strict();

const selectedScopeSchema = z.object({
    mode: z.literal('selected'),
    recordIds: z.array(z.string().trim().min(1).max(80)).min(1).max(5000).refine(
        (recordIds) => new Set(recordIds).size === recordIds.length,
        { message: 'Selected record IDs must be unique' },
    ),
    query: ReportRunQuerySchema.optional(),
}).strict();

const singleScopeSchema = z.object({
    mode: z.literal('single'),
    recordId: z.string().trim().min(1).max(80),
}).strict();

const tenantScopeSchema = z.object({
    mode: z.literal('tenant'),
    query: ReportRunQuerySchema.optional(),
    selectors: boundedRecord(
        z.string().trim().min(1).max(120),
        z.union([reportFilterScalar, z.array(reportFilterScalar).max(100)]),
        50,
        'selectors',
    ).optional(),
}).strict();

export const ReportRunScopeSchema = z.discriminatedUnion('mode', [
    filteredScopeSchema,
    selectedScopeSchema,
    singleScopeSchema,
    tenantScopeSchema,
]);

export const ReportRunRequestSchema = z.object({
    surfaceKey: z.string().trim().min(1).max(120),
    reportKey: z.string().trim().min(1).max(120).regex(/^[a-z][a-z0-9-]*$/),
    profile: z.object({
        id: z.string().trim().min(1).max(80),
        expectedVersion: z.number().int().positive().optional(),
    }).strict().optional(),
    scope: ReportRunScopeSchema,
    locale: z.enum(['tr', 'en']).optional(),
    title: z.string().trim().max(200).optional(),
    subtitle: z.string().trim().max(500).optional(),
    showScopeSummary: z.boolean().optional(),
    evidence: z.object({
        createSnapshot: z.boolean().optional(),
        baseline: DetailedReportBaselineSchema.optional(),
        narrativeId: z.string().trim().min(1).max(80).optional(),
    }).strict().default({}),
}).strict();

function effectiveDefinitions(catalog) {
    if (!Array.isArray(catalog)) return Object.values(catalog);
    const grouped = catalog.reduce((result, definition) => {
        const definitions = result.get(definition.key) || [];
        definitions.push(definition);
        result.set(definition.key, definitions);
        return result;
    }, new Map());
    return [...grouped.entries()].map(([reportKey, definitions]) => {
        const declaredCurrent = REPORT_CATALOG[reportKey];
        if (declaredCurrent) {
            const exact = definitions.find((definition) => (
                definition.definitionVersion === declaredCurrent.definitionVersion
            ));
            if (exact) return exact;
        }
        if (definitions.length === 1) return definitions[0];
        throw new Error(`Report catalog array has no explicit current definition: ${reportKey}`);
    });
}

export function createRunReportSchema(catalog = REPORT_CATALOG, { allowProfileless = false } = {}) {
    const definitions = effectiveDefinitions(catalog);
    const definitionsByKey = new Map(definitions.map((definition) => [definition.key, definition]));
    if (definitionsByKey.size === 0) throw new Error('Report catalog must not be empty');

    return ReportRunRequestSchema.superRefine((value, ctx) => {
        const definition = definitionsByKey.get(value.reportKey) || null;
        if (!definition) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reportKey'], message: 'Report definition is not available' });
            return;
        }
        if (!definition.lifecycle.runEnabled) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reportKey'], message: 'Report runs are disabled' });
        }
        if (!reportDefinitionSupportsSurface(definition, value.surfaceKey)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['surfaceKey'], message: 'Report does not belong to this surface' });
        }
        if (!definition.scopeModes.includes(value.scope.mode)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scope', 'mode'], message: 'Scope mode is not supported by this report' });
        }
        if (definition.capabilities.profile
            && !definition.capabilities.profileOptional
            && !allowProfileless
            && !value.profile) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['profile'], message: 'This report requires a profile' });
        }
        if (!definition.capabilities.profile && value.profile) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['profile'], message: 'This report does not support profiles' });
        }
        if (value.scope.mode === 'selected'
            && value.scope.recordIds.length > (definition.limits.maxSelectedRecords ?? definition.limits.maxRecords)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scope', 'recordIds'], message: 'Selected record limit exceeded' });
        }
        if (value.scope.mode === 'tenant') {
            if (value.scope.query && definition.reportClass !== 'generic-list') {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scope', 'query'], message: 'Tenant query is not supported by this report' });
            }
            const selectors = { ...definition.selectorDefaults, ...(value.scope.selectors || {}) };
            const properties = definition.selectorSchema.properties;
            for (const key of Object.keys(selectors)) {
                if (!Object.hasOwn(properties, key)) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scope', 'selectors', key], message: 'Selector is not declared by this report' });
                }
            }
            for (const key of definition.selectorSchema.required) {
                if (selectors[key] == null || selectors[key] === '') {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scope', 'selectors', key], message: 'Required selector is missing' });
                }
            }
        }
        if (!definition.capabilities.snapshot && value.evidence.createSnapshot) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['evidence', 'createSnapshot'], message: 'This report does not support snapshots' });
        }
        if (!definition.capabilities.compare && value.evidence.baseline?.strategy !== undefined
            && value.evidence.baseline.strategy !== 'none') {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['evidence', 'baseline'], message: 'This report does not support comparisons' });
        }
        if (!definition.capabilities.aiNarrative && value.evidence.narrativeId) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['evidence', 'narrativeId'], message: 'This report does not support AI narratives' });
        }
    }).transform((value) => {
        const definition = definitionsByKey.get(value.reportKey) || null;
        return {
            ...value,
            ...(value.scope.mode === 'tenant' ? {
                scope: {
                    ...value.scope,
                    selectors: { ...definition?.selectorDefaults, ...(value.scope.selectors || {}) },
                },
            } : {}),
            evidence: {
                ...value.evidence,
                createSnapshot: value.evidence.createSnapshot ?? Boolean(definition?.capabilities.snapshot),
            },
        };
    });
}

export function parseReportRunForDefinition(input, definition, options = {}) {
    return createRunReportSchema([definition], options).parse(input);
}

export const RunReportSchema = createRunReportSchema();
